// Analytics and Conversion Tracking
// Note: Consent is now handled by Google Consent Mode v2 in index.html
// gtag automatically respects consent settings and only tracks when analytics_storage: granted

export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  const gtag = (window as any).gtag;
  
  // gtag automatically respects Consent Mode v2
  // If analytics_storage is denied, events won't be sent
  if (typeof gtag === 'function') {
    gtag('event', eventName, parameters);
    
    // Log in development
    if (import.meta.env.DEV) {
      console.log('📊 Analytics Event:', eventName, parameters);
    }
  }
};

// Specific tracking functions
export const trackAmazonClick = (productName: string, asin: string, category: string) => {
  trackEvent('amazon_link_click', {
    event_category: 'engagement',
    event_label: productName,
    product_name: productName,
    product_id: asin,
    category: category,
  });
};

export const trackArticleView = (articleTitle: string, slug: string) => {
  trackEvent('article_view', {
    event_category: 'content',
    event_label: articleTitle,
    article_slug: slug,
  });
};

export const trackSearchQuery = (query: string, resultsCount: number) => {
  trackEvent('search', {
    search_term: query,
    results_count: resultsCount,
  });
};

export const trackCategoryView = (category: string, subcategory?: string) => {
  trackEvent('category_view', {
    event_category: 'navigation',
    category: category,
    subcategory: subcategory || 'none',
  });
};

export const trackSocialShare = (platform: string, url: string) => {
  trackEvent('share', {
    method: platform,
    content_type: 'article',
    item_id: url,
  });
};

export const trackNewsletterSignup = (source: string) => {
  trackEvent('newsletter_signup', {
    event_category: 'conversion',
    source: source,
  });
};

// Core Web Vitals tracking
export const trackWebVitals = async (): Promise<void> => {
  try {
    // Import web-vitals dynamically
    const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import('web-vitals');

    // Track Core Web Vitals
    onCLS((metric) => {
      trackEvent('web_vitals', {
        metric_name: 'CLS',
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
      });
    });

    onINP((metric) => {
      trackEvent('web_vitals', {
        metric_name: 'INP',
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
      });
    });

    onLCP((metric) => {
      trackEvent('web_vitals', {
        metric_name: 'LCP',
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
      });
    });

    onFCP((metric) => {
      trackEvent('web_vitals', {
        metric_name: 'FCP',
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
      });
    });

    onTTFB((metric) => {
      trackEvent('web_vitals', {
        metric_name: 'TTFB',
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
      });
    });
  } catch (error) {
    console.error('Error loading web-vitals:', error);
    // Fallback to basic performance tracking
    if (typeof window !== 'undefined' && window.performance) {
      window.addEventListener('load', () => {
        const perfData = window.performance.timing;
        const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
        const connectTime = perfData.responseEnd - perfData.requestStart;
        const renderTime = perfData.domComplete - perfData.domLoading;

        trackEvent('performance_metrics', {
          page_load_time: pageLoadTime,
          connect_time: connectTime,
          render_time: renderTime,
        });
      });
    }
  }
};
